// DomainSelectionPopup.js
import React, { useEffect, useState } from 'react';
import { Modal, Paper, Typography, Button, Box } from '@mui/material';
import { supabase } from '../supabaseClient';

const modalStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const paperStyle = {
  backgroundColor: '#1e1e1e',
  color: '#ffffff',
  padding: '24px',
  borderRadius: '12px',
  minWidth: '350px',
  maxWidth: '900px',
  boxShadow: '0 5px 15px rgba(0,0,0,0.5)',
  border: '1px solid #333',
};

// MASTER CATEGORY LIST (STATIC)
const CATEGORY_MAP = {
  hr: {
    label: "HR Agent",
    items: {
      Employees: "Human Resource",
      Policy: "Policy Repository"
    }
  },
sales: {
  label: "Sales Agent",
  items: {
    Customers: "Business Partner",
    "Sales Order": "Sales Order",
    "Sales Quotation": "Sales Quotation",
    Opportunities: "NA"
  }
},

  finance: {
    label: "Finance Agent",
    items: {
      "Finance Insight": "Cost Center",
      // Finance Reports emails a written report; Finance Analyst answers
      // in chat (with charts). Both read the same company statements.
      "Finance Reports": "Analytics",
      "Finance Analyst": "Finance Analyst",
      // AP vendor line items (ZVENDOR_OPEN_SRV). Open-item / ageing / payment
      // questions live here, not on the Vendor Invoice agent.
      "Vendor Ledger": "Vendor Ledger"
    }
  },
  purchase: {
    label: "Purchase Agent",
    items: {
      Vendor: "Vendor",
      "Purchase Requisition": "Purchase Requisition",
      "Purchase Order": "Purchase Orders",
      "Goods Movement": "Goods Movement",
      "Vendor Invoice": "Vendor Invoice",
      "Purchase Optimizer": "Purchase Optimizer"
    }
  },
  inventory: {
    label: "Inventory Agent",
    items: {
      Inventory: "Inventory"
    }
  },
  palmtop: {
    label: "Demo agent",
    items: {
      Palmtop: "Palmtop"
    }
  }
};

// Palmtop (Demo agent) is restricted to this single demo account.
const PALMTOP_DEMO_EMAIL = "palmtop_demo@gmail.com";

const DomainSelectionPopup = ({ open, onDomainSelect, onClose }) => {
  const [allowedAgents, setAllowedAgents] = useState([]);

  useEffect(() => {
    const load = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email;
      if (!email) return;

      const { data: profile } = await supabase
        .from("user_profiles")
        .select("agents")
        .eq("email", email)
        .single();

      const active = Object.entries(profile?.agents || {})
        .filter(([_, perms]) => perms?.length > 0)
        .map(([agent]) => agent);

      // Palmtop is only available to the demo account, regardless of Supabase grants.
      const allowed = active.filter(agent => agent !== "palmtop");
      if (email.toLowerCase() === PALMTOP_DEMO_EMAIL) {
        allowed.push("palmtop");
      }

      setAllowedAgents(allowed); // e.g. ["hr","sales","purchase"]
    };

    load();
  }, []);

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} sx={modalStyle}>
      <Paper sx={paperStyle}>
        <Typography variant="h5" sx={{ mb: 2, textAlign: "center", color: "#4D96FF" }}>
          Select an Agent
        </Typography>

<Box
  sx={{
    display: "grid",
    gap: 3,
    gridTemplateColumns: {
      xs: "1fr",
      sm: `repeat(${Math.max(1, Math.min(2, allowedAgents.length))}, 1fr)`,
      md: `repeat(${Math.max(1, allowedAgents.length)}, 1fr)`
    },
    transition: "all 0.2s ease"
  }}
>

          {allowedAgents.map(agentKey => {
            const category = CATEGORY_MAP[agentKey];
            if (!category) return null;

            return (
              <Box key={agentKey}>
                <Typography sx={{ color: '#bbb', mb: 1, fontWeight: 600 }}>
                  {category.label}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(category.items).map(([label, domainKey]) => (
                    <Button
                      key={label}
                      disabled={domainKey === "NA"}
                      onClick={() => domainKey !== "NA" && onDomainSelect(domainKey)}
                      sx={{
                        backgroundColor: "#2b2b2b",
                        color: "#fff",
                        borderRadius: "8px",
                        padding: "8px 0",
                        border: "1px solid #444",
                        "&:hover": {
                          backgroundColor: domainKey === "NA" ? "#2b2b2b" : "#3a3a3a"
                        }
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Modal>
  );
};

export default DomainSelectionPopup;
